import React, { FC, useEffect, useRef, useState } from 'react'
import { useFormik } from 'formik'
import * as yup from 'yup'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { BackArrow, Button, Input, Select, TopProgress } from '../../../components'
import dedicatedPreordersService, { DedicatedPreorderDetail, DedicatedPreorderUpdate } from '../../../api/dedicatedPreordersService'
import preorderHardwareService, { PreorderHardware, PreorderHardwareType } from '../../../api/preorderHardwareService'
import { usePageTitle } from '../../../context/navigationContext'
import { useUi } from '../../../context/ui.context'
import { DetailContainer, DetailHeader, DetailTitle } from '../styles'
import { ErrorMessage, FormActions, FormCard, FormGrid } from '../preorder-hardware/styles'
import { DiskRow, Note } from './styles'
import { preorderError, preordersPath, preorderStatuses, preorderStatusKey } from './helpers'

type Catalog = Record<PreorderHardwareType, PreorderHardware[]>

// Follow pagination so a selected resource is not limited to the first API page.
async function loadCatalog(type: PreorderHardwareType, signal: AbortSignal) {
  const items: PreorderHardware[] = []
  let page = 1
  while (!signal.aborted) {
    const { data } = await preorderHardwareService.list(type, { page, page_size: 100, is_active: true }, signal)
    items.push(...data.results)
    if (!data.results.length || (!data.next && page * 100 >= data.count)) break
    page += 1
  }
  return items
}

const Form: FC<{ id: string }> = ({ id }) => {
  const [t] = useTranslation()
  const navigate = useNavigate()
  const { viewAlert } = useUi()
  const [item, setItem] = useState<DedicatedPreorderDetail>()
  const [catalog, setCatalog] = useState<Catalog>({ cpus: [], rams: [], disks: [] })
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const busy = useRef(false)
  const detailPath = `${preordersPath}/${id}`
  usePageTitle(t('PREORDERS_EDIT'))

  useEffect(() => {
    const controller = new AbortController()
    let retry: number | undefined
    const load = async () => {
      try {
        const [preorder, cpus, rams, disks] = await Promise.all([
          dedicatedPreordersService.get(id, controller.signal),
          loadCatalog('cpus', controller.signal),
          loadCatalog('rams', controller.signal),
          loadCatalog('disks', controller.signal),
        ])
        if (controller.signal.aborted) return
        const merge = (current: (PreorderHardware | null)[], options: PreorderHardware[]) => (
          [...new Map([...current.filter((hardware): hardware is PreorderHardware => Boolean(hardware)), ...options].map((hardware) => [hardware.id, hardware])).values()]
        )
        setCatalog({ cpus: merge([preorder.cpu], cpus), rams: merge([preorder.ram], rams), disks: merge(preorder.disks.map(({ disk }) => disk), disks) })
        setItem(preorder)
        setLoadError('')
      } catch (error) {
        if (controller.signal.aborted) return
        setLoadError(preorderError(error, String(t('PREORDERS_LOAD_FAILED'))))
        retry = window.setTimeout(load, 10000)
      } finally {
        if (!controller.signal.aborted) setLoading(false)
      }
    }
    load()
    return () => { controller.abort(); window.clearTimeout(retry) }
  }, [id, t])

  const initialValues = {
    status: item?.status || 'submitted',
    cpu: item?.cpu?.id || '', cpu_count: item?.cpu_count ?? 1,
    ram: item?.ram?.id || '', ram_count: item?.ram_count ?? 1,
    disk_ids: item?.disks.flatMap(({ disk }) => disk ? [disk.id] : []) || [],
    comment: item?.comment || '', staff_comment: item?.staff_comment || '',
  }
  const required = String(t('PREORDER_REQUIRED'))
  const count = yup.number().typeError(String(t('PREORDERS_POSITIVE_INTEGER'))).integer(String(t('PREORDERS_POSITIVE_INTEGER'))).min(1, String(t('PREORDERS_POSITIVE_INTEGER'))).required(required)
  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: yup.object({
      cpu: yup.string().required(required), ram: yup.string().required(required),
      cpu_count: count, ram_count: count,
      disk_ids: yup.array().of(yup.string().required(required)),
      status: yup.string().oneOf(preorderStatuses).required(required),
    }),
    onSubmit: async (values, helpers) => {
      if (!item || busy.current) return
      busy.current = true
      helpers.setStatus(undefined)
      try {
        // PATCH only edited fields, retaining duplicate disk IDs and unrelated server settings.
        const payload = Object.fromEntries(Object.entries(values).filter(([key, value]) => (
          JSON.stringify(value) !== JSON.stringify(initialValues[key as keyof typeof initialValues])
        ))) as DedicatedPreorderUpdate
        if (payload.cpu_count !== undefined) payload.cpu_count = Number(payload.cpu_count)
        if (payload.ram_count !== undefined) payload.ram_count = Number(payload.ram_count)
        await dedicatedPreordersService.update(id, payload)
        viewAlert({ severity: 'success', message: t('PREORDERS_UPDATED') })
        navigate(detailPath, { replace: true })
      } catch (error: any) {
        const data = error?.response?.data
        if (data && typeof data === 'object') Object.entries(data).forEach(([field, messages]) => {
          if (field in values) helpers.setFieldError(field, Array.isArray(messages) ? messages.join(' ') : String(messages))
        })
        helpers.setStatus(preorderError(error, String(t('ERROR_OCCURRED'))))
      } finally { busy.current = false }
    },
  })
  const inputProps = {
    values: formik.values, errors: formik.errors, touched: formik.touched,
    handleChange: formik.handleChange, handleBlur: formik.handleBlur, disabled: formik.isSubmitting,
  }
  const choices = (type: PreorderHardwareType) => catalog[type].map((hardware) => ({
    value: hardware.id,
    label: [hardware.name, hardware.model, 'size_mb' in hardware ? `${hardware.size_mb} MB` : 'size_gb' in hardware ? `${hardware.size_gb} GB · ${hardware.disk_type}` : `${hardware.cores}C / ${hardware.threads}T`, !hardware.is_active ? t('INACTIVE') : null].filter(Boolean).join(' · '),
  }))

  return (
    <DetailContainer data-testid="dedicated-preorder-form">
      <BackArrow onClick={() => navigate(detailPath)} fallbackPath={detailPath} />
      <DetailHeader><DetailTitle>{t('PREORDERS_EDIT')}{item ? `: ${item.name || item.id}` : ''}</DetailTitle></DetailHeader>
      {loading ? <TopProgress loading /> : loadError || !item ? <ErrorMessage role="alert">{loadError}</ErrorMessage> : (
        <FormCard>
          <form onSubmit={formik.handleSubmit} noValidate>
            <FormGrid>
              <Select title={t('STATUS')} fieldName="status" selectedValue={formik.values.status}
                data={preorderStatuses.filter((status) => status !== 'released' || item.status === 'released').map((value) => ({ value, label: t(preorderStatusKey(value)) }))}
                onChange={(value) => formik.setFieldValue('status', value)} disabled={formik.isSubmitting || item.status === 'released'} />
              <Note>{t('PREORDERS_STATUS_HINT')}</Note>
              <Select title="CPU" fieldName="cpu" data={choices('cpus')} selectedValue={formik.values.cpu} onChange={(value) => formik.setFieldValue('cpu', value)} errors={formik.errors} touched={formik.touched} disabled={formik.isSubmitting} hasSearch required test_id="preorder-cpu-select" />
              <Input {...inputProps} title={t('DEDICATED_CPU_COUNT')} placeholder={t('DEDICATED_CPU_COUNT')} fieldName="cpu_count" type="number" value={formik.values.cpu_count} required />
              <Select title={t('RAM')} fieldName="ram" data={choices('rams')} selectedValue={formik.values.ram} onChange={(value) => formik.setFieldValue('ram', value)} errors={formik.errors} touched={formik.touched} disabled={formik.isSubmitting} hasSearch required test_id="preorder-ram-select" />
              <Input {...inputProps} title={t('DEDICATED_RAM_COUNT')} placeholder={t('DEDICATED_RAM_COUNT')} fieldName="ram_count" type="number" value={formik.values.ram_count} required />
              <div style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: 12 }}>
                {formik.values.disk_ids.map((disk, index) => <DiskRow key={index}>
                  <Select title={`${t('DISK')} ${index + 1}`} fieldName="disk" data={choices('disks')} selectedValue={disk}
                    onChange={(value) => formik.setFieldValue(`disk_ids.${index}`, value)} disabled={formik.isSubmitting} hasSearch required
                    errors={{ disk: typeof formik.errors.disk_ids === 'string' ? formik.errors.disk_ids : formik.errors.disk_ids?.[index] }} touched={{ disk: formik.submitCount > 0 }} test_id={`preorder-disk-${index}`} />
                  <Button variant="stroke" size="display" title={t('DELETE')} disabled={formik.isSubmitting} onClick={() => formik.setFieldValue('disk_ids', formik.values.disk_ids.filter((_, position) => position !== index))} />
                </DiskRow>)}
                <Button variant="stroke" size="display" title={t('PREORDER_ADD_DISK')} disabled={formik.isSubmitting} onClick={() => formik.setFieldValue('disk_ids', [...formik.values.disk_ids, ''])} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <Input {...inputProps} fieldName="comment" title={t('PREORDERS_CUSTOMER_COMMENT')} placeholder={t('PREORDERS_CUSTOMER_COMMENT')} isTextArea value={formik.values.comment} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <Input {...inputProps} fieldName="staff_comment" title={t('PREORDERS_STAFF_COMMENT')} placeholder={t('PREORDERS_STAFF_COMMENT')} isTextArea value={formik.values.staff_comment} />
              </div>
            </FormGrid>
            {formik.status && <ErrorMessage role="alert">{formik.status}</ErrorMessage>}
            <FormActions>
              <Button type="submit" variant="primary" size="display" title={t('SAVE')} loading={formik.isSubmitting} disabled={!formik.dirty} />
              <Button variant="stroke" size="display" title={t('CANCEL')} disabled={formik.isSubmitting} onClick={() => navigate(detailPath)} />
            </FormActions>
          </form>
        </FormCard>
      )}
    </DetailContainer>
  )
}

const DedicatedPreorderForm: FC = () => {
  const { preorderId = '' } = useParams()
  return <Form key={preorderId} id={preorderId} />
}

export default DedicatedPreorderForm
